import { arrayMove } from "@dnd-kit/sortable"
import { Workarea } from "../../../../domain/job"
import { WorkareaGroup } from "../../../../domain/workareas/group"
import { GroupedWorkareas, removeFromAllGroups, State } from "./workareaGroupsListModel"
import isEqual from "lodash/isEqual"

export type TaggedWorkarea = Workarea & { type: 'workarea' }
export type TaggedWorkareaGroup = WorkareaGroup & { type: 'group' }
export type EmptyGroup = { type: 'empty-group', groupId: string }
export type SortableItem = TaggedWorkarea | TaggedWorkareaGroup
export type PromoteWorkarea = { type: 'promote-workarea' }

export const tagWorkarea = (workarea: Workarea): TaggedWorkarea => ({ ...workarea, type: 'workarea' })
export const tagWorkareaGroup = (group: WorkareaGroup): TaggedWorkareaGroup => ({ ...group, type: 'group' })

export const untag = <T extends { type: string }>(item: T): Omit<T, 'type'> => {
  const { type, ...rest } = item
  return rest
}

export const isWorkarea = (item: SortableItem | EmptyGroup): item is TaggedWorkarea => item.type === 'workarea'
export const isWorkareaGroup = (item: SortableItem | EmptyGroup): item is TaggedWorkareaGroup => item.type === 'group'
export const isEmptyGroup = (item: SortableItem | EmptyGroup): item is EmptyGroup => item.type === 'empty-group'

export const adjustSortIndex = (workareas: GroupedWorkareas): GroupedWorkareas => {
  return Object.fromEntries(Object.entries(workareas).map(([groupId, workareas]) => [groupId, workareas.map((workarea, index) => ({ ...workarea, sortIndex: index }))]))
}

const removeFromGroupById = (workareas: GroupedWorkareas, workareaId: number, groupId: string): GroupedWorkareas => {
  return {
    ...workareas,
    [groupId]: workareas[groupId].filter(w => w.id !== workareaId)
  }
}

const addToGroupAtIndex = (workareas: GroupedWorkareas, workarea: Workarea, groupId: string, index: number): GroupedWorkareas => {
  return {
    ...workareas,
    [groupId]: [
      ...workareas[groupId].slice(0, index),
      { ...workarea, workareaGroupId: groupId },
      ...workareas[groupId].slice(index)
    ]
  }
}

const moveWorkareaOverWorkarea = (workareas: GroupedWorkareas, activeWorkarea: Workarea, over: Workarea, moveBelow: boolean): GroupedWorkareas => {
  if (activeWorkarea.id === over.id) {
    return workareas
  }

  if (activeWorkarea.workareaGroupId === over.workareaGroupId) {
    const activeIndex = workareas[activeWorkarea.workareaGroupId].findIndex(w => w.id === activeWorkarea.id)
    const overIndex = workareas[over.workareaGroupId].findIndex(w => w.id === over.id)
    return adjustSortIndex({
      ...workareas,
      [activeWorkarea.workareaGroupId]: arrayMove(workareas[activeWorkarea.workareaGroupId], activeIndex, overIndex)
    })
  }

  let result = removeFromGroupById(workareas, activeWorkarea.id, activeWorkarea.workareaGroupId)
  const overIndex = result[over.workareaGroupId].findIndex(w => w.id === over.id)
  result = addToGroupAtIndex(result, activeWorkarea, over.workareaGroupId, overIndex + (moveBelow ? 1 : 0))

  return adjustSortIndex(result)
}

const moveWorkareaOverGroup = (workareas: GroupedWorkareas, activeWorkarea: Workarea, overGroupId: string, moveBelow: boolean): GroupedWorkareas => {
  if (activeWorkarea.workareaGroupId === overGroupId) {
    return workareas
  }

  if (workareas[overGroupId]?.length > 0) {
    const overWorkarea = moveBelow ? workareas[overGroupId].slice(-1)[0] : workareas[overGroupId][0]
    return moveWorkareaOverWorkarea(workareas, activeWorkarea, overWorkarea, moveBelow)
  } else {
    return adjustSortIndex({
      ...removeFromGroupById(workareas, activeWorkarea.id, activeWorkarea.workareaGroupId),
      [overGroupId]: [{ ...activeWorkarea, workareaGroupId: overGroupId }]
    })
  }
}

const moveHiddenGroupOverWorkarea = (workareas: GroupedWorkareas, activeGroup: WorkareaGroup, over: Workarea, moveBelow: boolean): GroupedWorkareas => {
  const soleWorkarea = workareas[activeGroup.id][0]
  let result = removeFromAllGroups(workareas, soleWorkarea.id)
  result = addToGroupAtIndex(result, soleWorkarea, activeGroup.id, 0)
  result = addToGroupAtIndex(result, soleWorkarea, over.workareaGroupId, over.sortIndex + (moveBelow ? 1 : 0))
  return adjustSortIndex(result)
}

const moveHiddenGroupOverGroup = (workareas: GroupedWorkareas, activeGroup: WorkareaGroup, overGroupId: string, moveBelow: boolean): GroupedWorkareas => {
  if (activeGroup.id === overGroupId) {
    return workareas
  }
  const soleWorkarea = workareas[activeGroup.id][0]
  let result = removeFromAllGroups(workareas, soleWorkarea.id)
  result = addToGroupAtIndex(result, soleWorkarea, activeGroup.id, 0)
  result = addToGroupAtIndex(result, soleWorkarea, overGroupId, moveBelow ? workareas[overGroupId].length : 0)
  return adjustSortIndex(result)
}

type WorkareaSorting = Record<string, number[]>
const getWorkareaGroupSorting = (workareaGroups: WorkareaGroup[]): string[] => workareaGroups.map(g => g.id)
export const getWorkareaSorting = (workareas: GroupedWorkareas): WorkareaSorting => {
  return Object.fromEntries(Object.entries(workareas).map(([groupId, workareas]) => [groupId, workareas.map(w => w.id)]))
}

export type MoveWorkareaOrGroupRequest =
  | { type: 'groupSorted', workareaGroupSorting: string[] }
  | { type: 'hiddenGroupConsumed', hiddenWorkareaGroupId: string, newWorkareaGroupId: string, workareaSorting: WorkareaSorting }
  | { type: 'workareaGroupChanged', workareaId: number, newWorkareaGroupId: string, workareaSorting: WorkareaSorting }
  | { type: 'workareaSorted', workareaSorting: WorkareaSorting }
  | { type: 'workareaPromoted', workareaId: number }
  | { type: 'noChange' }

export const handleDrop = (active: SortableItem, over: SortableItem | PromoteWorkarea | EmptyGroup | undefined | null, serverState: State): MoveWorkareaOrGroupRequest => {
  if (!over) {
    return { type: 'noChange' }
  }

  if (over.type === 'promote-workarea') {
    return isWorkarea(active) ? { type: 'workareaPromoted', workareaId: active.id } : { type: 'noChange' }
  }

  if (isWorkareaGroup(active) && isWorkareaGroup(over)) {
    const activeIndex = serverState.workareaGroups.findIndex(g => g.id === active.id)
    const overIndex = serverState.workareaGroups.findIndex(g => g.id === over.id)
    const workareaGroups = arrayMove(serverState.workareaGroups, activeIndex, overIndex).map((g, i) => ({ ...g, sortIndex: i }))
    return {
      type: 'groupSorted',
      workareaGroupSorting: getWorkareaGroupSorting(workareaGroups)
    }
  }

  if (isWorkareaGroup(active) && active.isHidden && isWorkarea(over)) {
    const moved = moveHiddenGroupOverWorkarea(serverState.workareas, untag(active), untag(over), false)
    const sorting = getWorkareaSorting(moved)
    delete sorting[active.id]

    return {
      type: 'hiddenGroupConsumed',
      hiddenWorkareaGroupId: active.id,
      newWorkareaGroupId: over.workareaGroupId,
      workareaSorting: sorting
    }
  }

  if (isWorkareaGroup(active) && active.isHidden && isEmptyGroup(over)) {
    const moved = moveHiddenGroupOverGroup(serverState.workareas, untag(active), over.groupId, true)
    const sorting = getWorkareaSorting(moved)
    delete sorting[active.id]

    return {
      type: 'hiddenGroupConsumed',
      hiddenWorkareaGroupId: active.id,
      newWorkareaGroupId: over.groupId,
      workareaSorting: sorting
    }
  }

  if (isWorkarea(active) && isWorkarea(over)) {
    const moved = moveWorkareaOverWorkarea(serverState.workareas, untag(active), untag(over), true)
    const draggingSorting = getWorkareaSorting(moved)

    if (active.workareaGroupId !== over.workareaGroupId) {
      return {
        type: 'workareaGroupChanged',
        workareaId: active.id,
        newWorkareaGroupId: over.workareaGroupId,
        workareaSorting: draggingSorting
      }
    }

    const serverSorting = getWorkareaSorting(serverState.workareas)
    if (!isEqual(serverSorting, draggingSorting)) {
      return { type: 'workareaSorted', workareaSorting: draggingSorting }
    }
  }

  if (isWorkarea(active) && isEmptyGroup(over)) {
    const moved = moveWorkareaOverGroup(serverState.workareas, untag(active), over.groupId, true)
    const draggingSorting = getWorkareaSorting(moved)

    if (active.workareaGroupId !== over.groupId) {
      return {
        type: 'workareaGroupChanged',
        workareaId: active.id,
        newWorkareaGroupId: over.groupId,
        workareaSorting: draggingSorting
      }
    }
  }

  if (isWorkarea(active) && isWorkareaGroup(over)) {
    const moved = moveWorkareaOverGroup(serverState.workareas, untag(active), over.id, true)
    const workareaSorting = getWorkareaSorting(moved)

    if (active.workareaGroupId !== over.id) {
      return {
        type: 'workareaGroupChanged',
        workareaId: active.id,
        newWorkareaGroupId: over.id,
        workareaSorting
      }
    }
  }

  return { type: 'noChange' }
}

const moveWorkareaGroup = (serverState: State, groupId: string, diff: number): string[] => {
  const groupIds = serverState.workareaGroups.map(x => x.id)
  const index = groupIds.indexOf(groupId)
  const newIndex = index + diff
  if (newIndex < 0 || newIndex >= groupIds.length) {
    return groupIds
  }
  return arrayMove(groupIds, index, newIndex)
}

export const moveWorkareaGroupUp = (serverState: State, groupId: string): string[] => {
  return moveWorkareaGroup(serverState, groupId, -1)
}

export const moveWorkareaGroupDown = (serverState: State, groupId: string): string[] => {
  return moveWorkareaGroup(serverState, groupId, 1)
}

const moveWorkarea = (groupedWorkareas: GroupedWorkareas, groupId: string, workareaId: number, diff: number): WorkareaSorting => {
  const workareaIds = groupedWorkareas[groupId].map(x => x.id)
  const index = workareaIds.indexOf(workareaId)
  const newIndex = index + diff
  if (newIndex < 0 || newIndex >= workareaIds.length) {
    return getWorkareaSorting(groupedWorkareas)
  }

  return {
    ...getWorkareaSorting(groupedWorkareas),
    [groupId]: arrayMove(workareaIds, index, newIndex)
  }
}

export const moveWorkareaUp = (groupedWorkareas: GroupedWorkareas, groupId: string, workareaId: number): WorkareaSorting => {
  return moveWorkarea(groupedWorkareas, groupId, workareaId, -1)
}

export const moveWorkareaDown = (groupedWorkareas: GroupedWorkareas, groupId: string, workareaId: number): WorkareaSorting => {
  return moveWorkarea(groupedWorkareas, groupId, workareaId, 1)
}

export const moveWorkareaTo = (state: State, workarea: Workarea, groupId: string): MoveWorkareaOrGroupRequest => {
  if (groupId === 'move-out') {
    return handleDrop(tagWorkarea(workarea), { type: 'promote-workarea' }, state)
  }

  const group = state.workareaGroups.find(x => x.id === groupId)
  if (!group) {
    return { type: 'noChange' }
  }

  return handleDrop(tagWorkarea(workarea), tagWorkareaGroup(group), state)
}
